import type { ReactNode } from 'react';
import { Tooltip } from '~/components/Tooltip';
import { RequestButton } from '~/components/utils/buttons/RequestButton';
import { useApproval } from '~/hooks/useApproval';
import type { IMutationButtonMutation } from './props';

export interface IMutationButtonProps {
	askToDoMessage?: string;
	children?: ReactNode;
	className?: string;
	disabled?: boolean;
	disabledTooltipMessage?: string;
	disableOnSuccess?: boolean;
	form?: string;
	id?: string;
	mutation?: IMutationButtonMutation;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	showLoadingText?: boolean;
	successText?: string;
	text?: string;
	throttleLength?: number;
	title?: string;
	type?: 'submit' | 'button' | 'reset' | undefined;
	useApproval?: boolean;
}

export const MutationButton = ({
	id,
	className = 'bg-black px-5 py-2 rounded-md text-white text-lg font-medium',
	mutation,
	disabled = false,
	disableOnSuccess = false,
	type = 'submit',
	children = 'Submit',
	successText = 'Success!',
	onClick,
	throttleLength,
	form,
	showLoadingText = true,
	useApproval: useApprovalCheck,
	askToDoMessage = 'Are you sure?',
	title,
	disabledTooltipMessage,
}: IMutationButtonProps) => {
	// NOTE Throttle length only affects button usability
	// if disableOnSuccess === false
	if (throttleLength) {
		if (mutation?.status === 'success') {
			setTimeout(mutation.reset, throttleLength);
		}
	}

	const { askToDo } = useApproval({ defaultMessage: askToDoMessage });

	const handleClick =
		useApprovalCheck && onClick
			? (event: React.MouseEvent<HTMLButtonElement>) => {
					event.preventDefault();
					askToDo(() => onClick(event));
				}
			: onClick;

	const isShowingDisabledTooltip = !!disabledTooltipMessage && disabled;

	const button = (
		<RequestButton
			id={id}
			form={form}
			type={type}
			className={`${className} disabled:pointer-events-none`}
			disabled={disabled}
			isLoading={mutation?.status === 'loading'}
			showLoadingText={showLoadingText}
			onClick={handleClick}
			isSuccess={mutation?.status === 'success'}
			disableOnSuccess={disableOnSuccess}
			successText={successText}
			title={title}
		>
			{children}
		</RequestButton>
	);

	if (isShowingDisabledTooltip) {
		return <Tooltip content={disabledTooltipMessage}>{button}</Tooltip>;
	}

	return button;
};
